import { ToggleMenuEvent } from "@iunctim/component-navigation/Resources/Public/Scripts/events/ToggleMenuEvent.js";

/**
 * Class to implement the toggle-menu functionality
 * as a singleton, which can be instantiated by
 * any other extension as a dependency
 */
export class ToggleMenu {
    private static instance: ToggleMenu;
    private buttons: NodeListOf<HTMLElement>;

    public static readonly SELECTOR = `scope="toggle-menu"`;

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        this.buttons = document.querySelectorAll(`[${ToggleMenu.SELECTOR}]`);

        this.useMouseClick();
        this.useStateUpdates();
    }

    /**
     * Method to instantiate all
     * buttons on the current page
     *
     * @returns {ToggleMenu}
     */
    public static use(): ToggleMenu {
        if (!ToggleMenu.instance) {
            ToggleMenu.instance = new ToggleMenu();
        }
        return ToggleMenu.instance;
    }

    /**
     * Handles the state of the menu via mouse-click
     *
     * @returns {void}
     */
    protected useMouseClick(): void {
        this.buttons.forEach((button) => {
            if (!(button instanceof HTMLButtonElement)) {
                button.addEventListener("keydown", (event) => {
                    if (
                        event.target === button &&
                        ["Enter", " "].includes(event.key)
                    ) {
                        event.preventDefault();
                        button.click();
                    }
                });
            }
            button.addEventListener("click", () => {
                switch (button.getAttribute("aria-expanded")) {
                    case "true":
                        document.dispatchEvent(
                            new ToggleMenuEvent({
                                detail: {
                                    action: "close",
                                    identifier:
                                        button.getAttribute("aria-controls"),
                                },
                            }),
                        );
                        break;

                    case "false":
                        document.dispatchEvent(
                            new ToggleMenuEvent({
                                detail: {
                                    action: "open",
                                    identifier:
                                        button.getAttribute("aria-controls"),
                                },
                            }),
                        );
                        break;
                }
            });
        });
    }

    /**
     * Handles updates to the state of the target
     * element and reflects them on all buttons
     *
     * @returns {void}
     */
    protected useStateUpdates(): void {
        document.addEventListener(ToggleMenuEvent.KEY, (event) => {
            if (event instanceof ToggleMenuEvent) {
                const { action, identifier } = event.detail;

                switch (action) {
                    case "open":
                        this.buttons.forEach((button) => {
                            if (
                                identifier ===
                                button.getAttribute("aria-controls")
                            ) {
                                button.setAttribute("aria-expanded", "true");
                            }
                        });
                        break;

                    case "close":
                        this.buttons.forEach((button) => {
                            if (
                                identifier ===
                                button.getAttribute("aria-controls")
                            ) {
                                button.setAttribute("aria-expanded", "false");
                            }
                        });
                        break;
                }
            }
        });
    }
}
